import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _map from "lodash/map";
var _excluded = ["actionLabel", "actionProps", "className", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling", "disabled", "error", "errorMessage", "feedbackMessages", "fullWidth", "helpMessage", "icon", "id", "inputRef", "label", "style", "tooltip", "tooltipPlacement", "type"];
import "core-js/modules/es6.array.map.js";
import React from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useId } from '@nerdwallet/react-accessibility';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { ActionBlock } from '../ActionBlock/ActionBlock';
import { Box } from '../Box/Box';
import { InputGroup, shouldRenderInputGroup } from '../InputGroup/InputGroup';
import { Link } from '../Link/Link';
import style from './Input.module.less';

/**
 * `Input` enables a user to enter or edit data to influence or change their experience.
 */
export var Input = /*#__PURE__*/React.forwardRef(function (props, ref) {
  // Determine if we should compose an Input
  // within an InputGroup
  var willRenderInputGroup = shouldRenderInputGroup(props);
  useDeprecatedPropsWarning({
    component: 'Input',
    deprecatedProps: [{
      deprecated: 'icon',
      migration: 'Custom icons are no longer recommended for use in designs.',
      version: '9.5.0'
    }],
    props: props
  });
  useDeprecatedClassNameStylePropsWarning('Input', {
    className: props.className
  });

  var _props$actionLabel = props.actionLabel,
      actionLabel = _props$actionLabel === void 0 ? null : _props$actionLabel,
      _props$actionProps = props.actionProps,
      actionProps = _props$actionProps === void 0 ? null : _props$actionProps,
      className = props.className,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      disabled = props.disabled,
      error = props.error,
      errorMessage = props.errorMessage,
      feedbackMessages = props.feedbackMessages,
      fullWidth = props.fullWidth,
      helpMessage = props.helpMessage,
      icon = props.icon,
      idProp = props.id,
      inputRef = props.inputRef,
      label = props.label,
      styleProp = props.style,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      _props$type = props.type,
      type = _props$type === void 0 ? 'text' : _props$type,
      otherProps = _objectWithoutProperties(props, _excluded);

  var id = useId(idProp);
  var errorMessageId = "".concat(id, "-error-message");
  var helpMessageId = "".concat(id, "-help-message");
  var parsedFeedbackMessages = (feedbackMessages || []).map(function (f, i) {
    return _objectSpread(_objectSpread({}, f), {}, {
      id: "".concat(id, "-feedback-").concat(i)
    });
  });
  var ariaDescribedByIds = [errorMessageId, helpMessageId].concat(_toConsumableArray(_map(parsedFeedbackMessages, 'id'))).join(' '); // We can only attach an `aria-describedby` attribute if at least one of
  // the things it references is actually being displayed.
  // See https://nerdwallet.atlassian.net/browse/FEI-2206

  var renderAriaDescribedBy = helpMessage || errorMessage && error || parsedFeedbackMessages.length > 0; // Normally, we can render the input *as* an `<ActionBlock>` and avoid extra
  // DOM elements.

  var ActionBlockInputWithoutAction = /*#__PURE__*/React.createElement(ActionBlock, _extends({
    "aria-describedby": renderAriaDescribedBy ? ariaDescribedByIds : undefined,
    "aria-invalid": error ? 'true' : undefined,
    component: "input",
    className: classNames( // Only apply className if an input group will not be rendered
    // or the same className will be applied twice
    !willRenderInputGroup && className, style.input, style['input-height'], classes({
      'width-100': fullWidth
    })),
    disabled: disabled,
    error: error,
    id: id,
    type: type // `inputRef` exists mostly for backwards compatibility
    ,
    ref: inputRef || ref,
    style: styleProp,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling
  }, otherProps)); // If we need to show an action next to the input, we need to render the
  // input *inside* the `<ActionBlock>`.

  var ActionBlockInputWithAction = /*#__PURE__*/React.createElement(ActionBlock, {
    mode: "container",
    component: "div",
    className: classNames( // Only apply className if an input group will not be rendered
    // or the same className will be applied twice
    !willRenderInputGroup && className, style['input-height'], classes('display-inline-flex', 'align-items-center', {
      'width-100': fullWidth
    }), _defineProperty({}, style['action-block-container'], !fullWidth)),
    disabled: disabled,
    error: error,
    style: styleProp,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling
  }, /*#__PURE__*/React.createElement("input", _extends({
    "aria-invalid": error ? 'true' : undefined,
    "aria-describedby": ariaDescribedByIds,
    className: classNames(style.input, style['inner-input'], _defineProperty({}, style.disabled, disabled), classes('align-self-stretch')),
    disabled: disabled,
    id: id,
    type: type // `inputRef` exists mostly for backwards compatibility
    ,
    ref: inputRef || ref
  }, otherProps)), actionLabel && /*#__PURE__*/React.createElement(Box, {
    marginRight: "3"
  }, /*#__PURE__*/React.createElement(Link, _extends({
    component: "button",
    disabled: disabled,
    size: 1
  }, actionProps), actionLabel)));
  var hasAction = actionLabel || actionProps;
  var ActionBlockInput = hasAction ? ActionBlockInputWithAction : ActionBlockInputWithoutAction; // Using a custom icon within an Input is deprecated since
  // the v9.5 Atom Studio updates. Maintain this container for
  // backwards-compatibility

  var IconContainerInput = /*#__PURE__*/React.createElement("span", {
    className: classNames(style['input-container'], classes({
      'width-100': fullWidth
    }))
  }, ActionBlockInput, icon && /*#__PURE__*/React.createElement("span", {
    className: style['icon-container']
  }, icon)); // If still using deprecated custom icon containers,
  // then short circuit and render the legacy
  // input container

  if (icon) {
    return IconContainerInput;
  } // Render ActionBlock directly if none of the
  // InputGroup props are supplied


  if (!willRenderInputGroup) {
    return ActionBlockInput;
  }

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className,
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageId: errorMessageId,
    feedbackMessages: parsedFeedbackMessages,
    helpMessage: helpMessage,
    helpMessageId: helpMessageId,
    input: ActionBlockInput,
    inputId: id,
    fullWidth: fullWidth,
    label: label,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement
  });
});
Input.displayName = 'Input';